import { createSlice } from '@reduxjs/toolkit';
import { getEngagementSummary,
    getPrescribers,
    getEngagers,
    getAllPrescibers,
    getAllEngagers } from '../thunks/engagementThunks';
import { calculatePrescriptionPercentage } from '../../utility';

const engagement = createSlice({
    name: 'engagement',
    initialState: {
        isLoading: true,
        error: null,
        totalSummary: {
            currentEngagement: 0,
            previousEngagement: 0,
            totalBarData: {
                barData: [{
                    name: '',
                    data: []
                }],
                categories: []
            } 
        },
        fieldSummary: {
            currentEngagement: 0,
            previousEngagement: 0,
            fieldBarData: {
                barData: [{
                    name: '',
                    data: []
                }],
                categories: []
            }
        },
        digitalSummary: {
            currentEngagement: 0,
            previousEngagement: 0,
            digitalBarData: {
                barData: [{
                    name: '',
                    data: []
                }],
                categories: []
            }
        },
        isPrescriberLoading: true,
        prescribers: [],
        engagement: null,
        columns: [
            'NPI',
            'Name',
            'Location',
            'Channel Preference',
            'Partner Preference',
            'Physician Segment'
        ],
        prescibersData: [],
        presciberData: [],
        currentPage: 1,
        pageSize: 20,
        totalCount: 0,
        totalPages: 0,
        searchText: '',
        npi_ids: [],
        totalEngagements: 0
    },
    reducers: {
        setCurrentPage: (state, action) => {
            state.currentPage = action.payload;
        },
        changeSearchText: (state, action) =>{
            const text = action.payload;
            state.searchText = text; 
            const data = handleSearch(text, state.presciberData);
            state.prescibersData = data;
            /* Total Count */ 
            state.totalCount = data?.length ?? 0;
            /* Total Pages */ 
            state.totalPages = Math.ceil((data?.length ?? 0) / state.pageSize);
            /* Current Page */ 
            state.currentPage = 1;
        }
    },
    extraReducers: (builder) => {
        builder
            /* Get Engagement Summary*/ 
            .addCase(getEngagementSummary.fulfilled, (state, action) => {
                state.isLoading = false;
                if(action.payload && action.payload.engagement_summaries) {
                    state.digitalSummary = action.payload?.['engagement_summaries']?.['digitalEngagement'];
                    state.fieldSummary = action.payload?.['engagement_summaries']?.['fieldEngagement'];
                    state.totalSummary = action.payload?.['engagement_summaries']?.['totalEngagement'];
                    state.digitalSummary.digitalBarData = makeDashboardBarData(action.payload?.['engagement_summaries']?.['digitalEngagement']?.['digital_date_engagement']);
                    state.totalSummary.totalBarData = makeDashboardBarData(action.payload?.['engagement_summaries']?.['totalEngagement']?.['total_date_engagement']);
                    state.fieldSummary.fieldBarData = makeDashboardBarData(action.payload?.['engagement_summaries']?.['fieldEngagement']?.['field_date_engagement ']);
                }
                state.error = null;
            })
            .addCase(getEngagementSummary.pending, (state, action) => {
                state.isLoading = true;
                state.error = null;
            })
            .addCase(getEngagementSummary.rejected, (state, action) => {
                state.isLoading = false;
                state.error = action.payload;
            })
            /* Get Prescribers */ 
            .addCase(getPrescribers.fulfilled, (state, action) => {
                state.isPrescriberLoading = false;
                state.prescribers = calculatePrescriptionPercentage(action.payload.prescribers, true);
                state.error = null;
            })
            .addCase(getPrescribers.pending, (state, action) => {
                state.isPrescriberLoading = true;
                state.error = null;
            })
            .addCase(getPrescribers.rejected, (state, action) => {
                state.isPrescriberLoading = false;
                state.error = action.payload;
            })
            /* Get Engagers */
            .addCase(getEngagers.fulfilled, (state, action) => {
                state.isPrescriberLoading = false;
                state.isLoading = false;
                state.prescribers = calculatePrescriptionPercentage(action.payload?.top_engagements || [], false);
                state.error = null;
            })
            .addCase(getEngagers.pending, (state, action) => {
                state.isPrescriberLoading = true;
                state.isLoading = true;
                state.error = null;
            })
            .addCase(getEngagers.rejected, (state, action) => {
                state.isPrescriberLoading = false;
                state.isLoading = false;
                state.error = action.payload;
            }) 
            /* Get All Prescribers */ 
            .addCase(getAllPrescibers.fulfilled, (state,action) => {
                state.prescibersData = action.payload?.results?.physicians;
                state.presciberData = action.payload?.results?.physicians;
                state.searchText='';
                /* Total Count */ 
                state.totalCount = action.payload?.results?.physicians?.length ?? 0;
                /* Total Pages */ 
                state.totalPages = Math.ceil((action.payload?.results?.physicians?.length ?? 0) / state.pageSize);
                state.npi_ids = action.payload?.results?.physicians.map(prescriber => prescriber.npi_id);
                state.error = null;
                state.isLoading = false;
            })
            .addCase(getAllPrescibers.pending, (state, action) => {
                state.isLoading = true;
                state.error = null;
            })
            .addCase(getAllPrescibers.rejected, (state, action) => {
                state.isLoading = false;
                state.error = action.payload;
            })
            /* Get All Engagers*/ 
            .addCase(getAllEngagers.fulfilled, (state, action) => {
                state.isPrescriberLoading = false;
                state.isLoading = false;
                state.error = null;
                state.searchText='';
                state.prescibersData = action.payload?.all_engagements?.top_engagers;
                state.presciberData = action.payload?.all_engagements?.top_engagers;
                state.totalEngagements = action.payload?.all_engagements?.max_engagement_count || 0;
                /* Total Count */ 
                state.totalCount = action.payload?.all_engagements?.top_engagers?.length ?? 0;
                /* Total Pages */ 
                state.totalPages = Math.ceil((action.payload?.all_engagements?.top_engagers?.length ?? 0) / state.pageSize);
            })
            .addCase(getAllEngagers.pending, (state, action) => {
                state.isPrescriberLoading = true;
                state.isLoading = true;
                state.error = null;
            })
            .addCase(getAllEngagers.rejected, (state, action) => {
                state.isPrescriberLoading = false;
                state.isLoading = false;
                state.error = action.payload;
            }); 
    }
});

const makeDashboardBarData = (data) => {
    const barData = [{
        name: '',
        data: []
    }];
    const categories = [];
    
    if (data?.length) {
        data.forEach(item => {
            if (item?.count > 0) {
                categories.push(item?.start_date + ' - ' + item?.end_date);
                barData[0].data.push(parseInt(item.count));
            }
        });
    }

    return { barData, categories };
};


const handleSearch = (search, prescribers) => {
    let filteredData = [];
   
    filteredData = prescribers.filter((prescriber) => {
        const {
            npi_id,
            first_name,
            last_name,
            address1,
            city,
            state,
            zip,
            district,
            channel_preference,
            partner_preference,
            segmentation
        } = prescriber;

        const itemData = `${npi_id?.toUpperCase()} ${first_name?.toUpperCase()} ${last_name?.toUpperCase()} ${address1?.toUpperCase()}  ${city?.toUpperCase()} ${state?.toUpperCase()} ${zip?.toUpperCase()}  ${district?.toUpperCase()} ${channel_preference
            ?.toString()
            ?.toUpperCase()} ${partner_preference
            ?.toString()
            ?.toUpperCase()} ${segmentation?.toString()?.toUpperCase()}`;

        const textData = search?.trim()?.toUpperCase();

        return itemData.indexOf(textData) > -1;
    });

    return filteredData;
};


export const { actions } = engagement;
export default engagement.reducer;